import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BELGIUM_COUNTRY_DEFAULT_OPTION } from '@shared/consts';
import { formWithCrossFieldsValidation } from '@shared/helpers';
import { AddressFormGroupType, Locality, Street } from '@shared/interfaces/addresses';
import { AddressesService } from '@shared/services/addresses.service';
import { ContactsService } from '@shared/services/contacts.service';
import { AddressType } from '@shared/types/addresses/address-type.type';
import { catchError, lastValueFrom, of } from 'rxjs';
import { FormOptions } from 'src/app/common/models/form-options';
import { Logger } from 'src/app/core/services';

export abstract class AddressFactory {
  private static async getCountryLocalities(service: AddressesService, countryId: number): Promise<Locality[]> {
    return await lastValueFrom(service.fetchCountryLocalities(countryId).pipe(catchError(() => [])));
  }
  private static async getLocalityStreets(config: {
    service: AddressesService;
    countryId: number;
    postalCodeId: number;
  }): Promise<Street[]> {
    const { service, countryId, postalCodeId } = config;

    if (!postalCodeId) return [];

    return await lastValueFrom(service.fetchLocalityStreets(countryId, postalCodeId).pipe(catchError(() => of([]))));
  }

  public static newForm(
    options: {
      type: AddressType;
      withCrossValidation: boolean;
    } = {
      type: 'secondary',
      withCrossValidation: true,
    }
  ): FormGroup<AddressFormGroupType> {
    const base = new FormGroup<AddressFormGroupType>({
      id: new FormControl(null),
      country: new FormControl(BELGIUM_COUNTRY_DEFAULT_OPTION, Validators.required),
      locality: new FormControl(null, Validators.required),
      street: new FormControl(null, Validators.required),
      streetNumber: new FormControl(null, [Validators.required]),
      boxNumber: new FormControl(null),
      type: new FormControl(options.type, Validators.required),
    });

    return options.withCrossValidation
      ? formWithCrossFieldsValidation(base, [
          ['country', 'locality'],
          ['locality', 'street'],
          ['street', 'streetNumber'],
          ['streetNumber', 'boxNumber'],
        ])
      : base;
  }

  public static async buildForms(config: {
    contactId: number;
    deps: [AddressesService, ContactsService, FormOptions, Logger];
  }): Promise<FormGroup<AddressFormGroupType>[]> {
    const { contactId, deps } = config;
    const [addressesService, contactsService, formOptions, logger] = deps;

    const contactAddresses = await lastValueFrom(contactsService.fetchContactAddresses(contactId));
    const forms: FormGroup<AddressFormGroupType>[] = [];

    if (!contactAddresses.length) return [AddressFactory.newForm({ type: 'main', withCrossValidation: true })];

    for (const address of contactAddresses) {
      const addressForm = AddressFactory.newForm({ type: address.type, withCrossValidation: true });
      addressForm.patchValue({ id: address.id });

      if (!address.countryId) {
        forms.push(addressForm);
        continue;
      }

      const addressCountryLocalities = (await this.getCountryLocalities(addressesService, address.countryId)) ?? [];

      if (!address.localityId) {
        forms.push(addressForm);
        continue;
      }

      let foundLocality = addressCountryLocalities.find((locality) => locality.id === address.localityId);

      if (!foundLocality) {
        logger.logIfNotFound('find()', 'Address Factory - buildForm (locality from iDélibé)');
        const createdPostalCodeIdAndStreetId = await lastValueFrom(
          addressesService.insertNewPostalCodeAndStreet({
            codePostal: {
              codePostal: address.postalCode,
              langueID: 1, // French
              localite: address.localityName,
              paysID: address.countryId,
              externalApiSourceId: null,
            },
            rue: {
              codeLangue: 'BE',
              codePostal: address.postalCode,
              codeRue: '',
              externalApiSourceId: null,
              rue: address.streetName,
            },
          })
        );

        foundLocality = addressCountryLocalities.find(
          (locality) => locality.id === createdPostalCodeIdAndStreetId.postalCodeId
        );

        if (!foundLocality) {
          logger.logIfNotFound('find()', 'Address Factory - buildForm (locality from iDélibé after injection)');

          continue;
        }
      }

      if (!address.streetId) {
        forms.push(addressForm);
        continue;
      }

      const localityStreets =
        (await this.getLocalityStreets({
          service: addressesService,
          countryId: address.countryId,
          postalCodeId: foundLocality.id,
        })) ?? [];
      const foundStreet = localityStreets.find((street) => street.id === address.streetId);

      if (!foundStreet) {
        logger.logIfNotFound('find()', 'Address Factory - buildForm (street)');
      }

      const countries = formOptions.countries();
      const foundCountry = countries.find((country) => country.value.id === address.countryId);

      if (!foundCountry) {
        logger.logIfFalsy(foundCountry, 'Address Factory - buildForm (country)');
        continue;
      }

      const newForm = AddressFactory.newForm();
      newForm.setValue({
        id: address.id,
        type: address.type,
        country: foundCountry.value,
        locality: foundLocality,
        street: foundStreet ?? null,
        streetNumber: address.streetNumber,
        boxNumber: address.streetBoxNumber,
      });

      forms.push(newForm);
    }

    const mainAddressIndex = forms.findIndex((form) => form.value.type === 'main');

    if (mainAddressIndex !== -1) {
      forms.unshift(forms.splice(mainAddressIndex, 1)[0]);

      const secondaryAddressIndex = forms.findIndex((form) => form.value.type === 'secondary');

      if (secondaryAddressIndex !== -1) {
        forms.splice(1, 0, forms.splice(secondaryAddressIndex, 1)[0]);
      }
    }

    return forms;
  }
}
